import React, { useState, useRef, useEffect } from 'react';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import { styled, useTheme } from '@mui/material/styles';
import {
  Drawer,
  Stack,
  ListSubheader,
  Popper,
  Paper,
  Collapse,
  Box,
  Typography,
  Tooltip
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useNavigate, useLocation } from 'react-router-dom';
import { useOkta } from '../../hooks/oktaHook';
import { useSelector } from 'react-redux';
import { getUrlWithParams, hasCommonElement } from '../../utils/Utils';
import { GENERAL_USER } from '../../utils/Constants';

const drawerWidth = 284;
const miniDrawerWidth = 56;

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-start'
}));

const NavBar = ({ pages }) => {
  const { userDetails } = useOkta();
  const [draweropen, setDrawerOpen] = useState(false);
  const [open, setOpen] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [isPopperOpen, setIsPopperOpen] = useState(false);
  const theme = useTheme();
  const navigate = useNavigate();
  const popperRef = useRef(null);
  const isCalendarCreated = useSelector((state) => state.calendar.calendarExistance);
  const { search } = useLocation();

  const newParams = new URLSearchParams(search);
  const selectedCycleId = Number(newParams.get('cycleId'));
  const selectedCycle = newParams.get('cycleName');
  const bu = newParams.get('bu');

  const handleDrawerOpen = () => setDrawerOpen(true);
  const handleDrawerClose = () => {
    setDrawerOpen(false);
    // Reset the open state when closing the drawer
    setOpen({});
  };

  const handleToggle = (pageName) => {
    setOpen((prevState) => ({
      ...prevState,
      [pageName]: !prevState[pageName]
    }));
  };

  const handleHover = (event, subPages) => {
    if (!draweropen && subPages) {
      setAnchorEl(event.currentTarget);
      setIsPopperOpen(true);
    }
  };

  const handleMouseEnter = () => {
    setIsPopperOpen(true);
  };

  const handleMouseLeave = () => {
    setIsPopperOpen(false);
  };

  const isActivePage = (page) => {
    if (location.pathname === page.path) {
      return true;
    }
    if (page.subPages) {
      return page.subPages.some((subPage) => location.pathname === subPage.path);
    }
    return false;
  };

  // Find the page based on the aria-label
  const anchorPageName = anchorEl?.getAttribute('aria-label');
  const anchorPage = pages.find((page) => page.pageName === anchorPageName);

  const getPathWithParams = (path) => {
    if (path === `/home?bu=${bu}`) {
      return getUrlWithParams(path, bu);
    } else {
      return getUrlWithParams(path, bu, selectedCycle, selectedCycleId);
    }
  };

  const handlePageClick = (page) => {
    if (draweropen) {
      if (page.subPages) {
        handleToggle(page.pageName);
      } else {
        navigate(getPathWithParams(page.path));
      }
    } else {
      if (page.subPages) {
        navigate(getPathWithParams(page.subPages[0].path));
      } else {
        navigate(getPathWithParams(page.path));
      }
    }
  };

  useEffect(() => {
    // Collapse subpages when drawer is collapsed
    if (!draweropen) {
      setOpen({});
    }
  }, [draweropen]);

  function hasViewPermByRole(page) {
    // Check if the user's roles include any of the allowed user scopes
    return userDetails.roles.some((role) => page.allowedUserScope?.includes(role));
  }

  function hasViewPermByBU(page) {
    // Allow access if allowedBU is ALL
    if (page.allowedBU?.includes('ALL')) {
      return true;
    }
    // Check if the user's BUs include any of the allowed BUs
    return userDetails.BUs.some((bu) => page.allowedBU?.includes(bu));
  }

  function canAccessPage(page) {
    // User must either have a valid role and (either allowed BU or ALL)
    const hasRolePermission = hasViewPermByRole(page);
    const hasBUPermission = hasViewPermByBU(page);
    if (page.pageName === 'Calendar' && hasCommonElement(userDetails.roles, [GENERAL_USER])) {
      return hasRolePermission && hasBUPermission && isCalendarCreated;
    }
    return hasRolePermission && hasBUPermission; // Access granted only if both conditions are met
  }

  return (
    <>
      {draweropen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 999 // Above everything else
          }}
          onClick={handleDrawerClose}
        />
      )}
      <Drawer
        sx={{
          width: draweropen ? drawerWidth : miniDrawerWidth,
          flexShrink: 0,
          whiteSpace: 'nowrap',
          marginTop: '50px',
          zIndex: draweropen ? 1000 : 0,
          position: 'absolute',

          '& .MuiDrawer-paper': {
            width: draweropen ? drawerWidth : miniDrawerWidth,
            boxSizing: 'border-box',
            overflowX: 'hidden',
            marginTop: '50px',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen
            }),
            backgroundColor: '#FBFCFE',
            boxShadow: '4px 0px 24px -2px rgba(0, 92, 188, 0.08)',
            padding: '0px 0px 32px',
            border: 'none'
          }
        }}
        variant="permanent"
        open={draweropen}
        id="main-left-drawer">
        <DrawerHeader onClick={draweropen ? handleDrawerClose : handleDrawerOpen}>
          <IconButton
            sx={{
              color: '#3A3A3A',
              minWidth: '50px'
            }}>
            {draweropen ? (
              <MenuOpenIcon sx={{ marginLeft: -1.5 }} />
            ) : (
              <MenuIcon sx={{ marginLeft: -1.5 }} />
            )}
          </IconButton>
          <Typography variant="body1" sx={{ color: '#616161' }}>
            Collapse
          </Typography>
        </DrawerHeader>

        <Stack
          sx={{
            height: '85%',
            justifyContent: 'space-between'
          }}>
          <List>
            {pages.map((page) => (
              <Tooltip
                placement="top"
                key={page.pageName}
                title={
                  <div className="" style={{ padding: '0px 5px' }}>
                    {page.pageName}
                  </div>
                }>
                <Box key={page.pageName} sx={{ marginBottom: '32px' }}>
                  <ListItemButton
                    onClick={() => {
                      handlePageClick(page);
                    }}
                    onMouseEnter={(event) => handleHover(event, page.subPages)}
                    onMouseLeave={handleMouseLeave}
                    aria-label={page.pageName}
                    disabled={!canAccessPage(page)}
                    sx={{
                      boxShadow:
                        isActivePage(page) && (!draweropen || !page.subPages)
                          ? 'inset 5px 0px 0px #005CBC'
                          : '',
                      width: '100%'
                    }}>
                    <ListItemIcon sx={{ fontSize: '20px', minWidth: '40px' }}>
                      {React.cloneElement(page.icon, {
                        style: { color: isActivePage(page) ? '#005CBC' : 'rgba(58, 58, 58, 1)' }
                      })}
                    </ListItemIcon>

                    <ListItemText
                      primary={page.pageName}
                      sx={{
                        color: isActivePage(page) ? '#005CBC' : '#616161',
                        fontWeight: '400',
                        fontSize: '16px',
                        lineHeight: '22px'
                      }}
                    />
                    {page.subPages && (open[page.pageName] ? <ExpandLess /> : <ExpandMore />)}
                  </ListItemButton>

                  {page.subPages && (
                    <Collapse in={open[page.pageName]} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {page.subPages.map((subPage) => (
                          <ListItemButton
                            key={subPage.pageName}
                            sx={{
                              pl: '56px',
                              backgroundColor: '#EFF5FC',
                              boxShadow: isActivePage(subPage) ? 'inset 5px 0px 0px #005CBC' : ''
                            }}
                            disabled={!canAccessPage(subPage) || subPage.disabled}
                            onClick={() => {
                              if (subPage.navigateState) {
                                navigate(subPage.path, {
                                  state: {
                                    tabName: subPage.navigateState
                                  }
                                });
                              } else {
                                navigate(subPage.path);
                              }
                            }}>
                            <ListItemText
                              primary={subPage.pageName}
                              sx={{
                                color: '#616161',
                                fontWeight: '400',
                                fontSize: '16px',
                                lineHeight: '22px'
                              }}
                            />
                          </ListItemButton>
                        ))}
                      </List>
                    </Collapse>
                  )}
                </Box>
              </Tooltip>
            ))}
          </List>

          {!draweropen && (
            <Popper
              open={isPopperOpen}
              anchorEl={anchorEl}
              placement="right-start"
              disablePortal={false}
              modifiers={[{ name: 'offset', options: { offset: [0, 0] } }]}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              ref={popperRef}
              sx={{
                borderRadius: '8px',
                zIndex: 999999
              }}>
              {anchorEl && anchorPage && (
                <Paper
                  sx={{
                    padding: 0.5,
                    backgroundColor: '#FBFCFE',
                    boxShadow: '4px 0px 24px -2px rgba(0, 92, 188, 0.08)'
                  }}>
                  <ListSubheader
                    sx={{
                      color: '#616161',
                      padding: '12px',
                      fontWeight: '400',
                      fontSize: '16px',
                      lineHeight: '22px',
                      backgroundColor: 'inherit'
                    }}>
                    {anchorPage.pageName}
                  </ListSubheader>
                  {anchorPage.subPages.map((subPage) => (
                    <ListItemButton
                      key={subPage.pageName}
                      sx={{ backgroundColor: '#EFF5FC' }}
                      disabled={!canAccessPage(subPage) || subPage.disabled}
                      onClick={() => {
                        if (subPage.navigateState) {
                          navigate(getPathWithParams(subPage.path), {
                            state: {
                              tabName: subPage.navigateState
                            }
                          });
                        } else {
                          navigate(getPathWithParams(subPage.path));
                        }
                      }}>
                      <ListItemText
                        primary={subPage.pageName}
                        sx={{
                          color: '#616161',
                          fontWeight: '400',
                          fontSize: '16px',
                          lineHeight: '22px'
                        }}
                      />
                    </ListItemButton>
                  ))}
                </Paper>
              )}
            </Popper>
          )}
        </Stack>
      </Drawer>
    </>
  );
};

export default NavBar;
